import { Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { API_URL } from '../../../app.config';
import { catchError, Observable, of, throwError } from 'rxjs';
import { User } from '../models/userMe.model';
import { toObservable } from '@angular/core/rxjs-interop';
import { ToastService } from './toast.service';
import { SKIP_SPINNER } from '../../interceptors/spinner/spinner-context.token';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private userSignal = signal<User | null>(null);
  private userListSignal = signal<User[] | null>(null);
  private errorSignal = signal<string | null>(null);
  private _hasPermission = signal<boolean | undefined>(undefined);
  private readonly apiUrl = inject(API_URL);

  constructor(
    private http: HttpClient,
    private toastService: ToastService,
  ) {
    this.loadUserFromStorage();
  }

  get user() {
    return this.userSignal;
  }

  get error() {
    return this.errorSignal;
  }

  get userList() {
    return this.userListSignal;
  }

  get hasPermission() {
    return this._hasPermission;
  }
  get hasPermission$() {
    return toObservable(this._hasPermission);
  }

  fetchUser(): void {
    if (this.userSignal()) return;
    const token = localStorage.getItem('token');
    if (!token) {
      setTimeout(() => this.fetchUser(), 1500);
      return;
    }

    this.http
      .get<User>(`${this.apiUrl}User/me`)
      .pipe(
        catchError(error => {
          console.error('Error fetching user:', error);

          if (error.status === 403) {
            this._hasPermission.set(false);
            return of(null);
          }

          this.errorSignal.set('Error loading user data.');
          return throwError(() => new Error(error.message));
        }),
      )
      .subscribe(userData => {
        if (!userData) {
          console.error('No user data found');
          return;
        }
        if (userData?.photo) {
          userData.photo = this.convertBase64ToImage(userData.photo);
        }
        this._hasPermission.set(true);
        localStorage.setItem('UserData', JSON.stringify(userData));
        this.userSignal.set(userData);
        this.errorSignal.set(null);
      });
  }

  private loadUserFromStorage() {
    const storedUser = localStorage.getItem('UserData');
    if (storedUser) {
      this.userSignal.set(JSON.parse(storedUser));
      this._hasPermission.set(true);
    }
  }

  convertBase64ToImage(base64String: string): string {
    if (!base64String.startsWith('data:image')) {
      return `data:image/jpeg;base64,${base64String}`;
    }
    return base64String;
  }

  public getUsers(searchText: string): Observable<User[]> {
    return this.http
      .post<User[]>(
        `${this.apiUrl}User/SearchUsers`,
        { searchText },
        { context: new HttpContext().set(SKIP_SPINNER, true) },
      )
      .pipe(
        catchError((error: unknown) => {
          const errorMessage = error instanceof Error ? error.message : 'Error loading user data.';
          this.toastService.show(errorMessage, 'error');
          this.errorSignal.set('Error loading user data.');
          return throwError(() => error);
        }),
      );
  }

}
